/**
 * Cities, and the zones inside them that ops actually serve.
 *
 * **A city carries two states that cannot contradict each other.** `launch_stage` is the word the
 * website reads; `is_launched` is the boolean the booking API reads. The server keeps them
 * consistent by constraint, so this screen changes the *stage* and shows the boolean that came
 * back — never both. A city can never be `live` while refusing bookings, nor `seo_only` while
 * taking them, and the console cannot create that contradiction even by accident.
 *
 * **Pausing is not archiving, and archiving is not deleting.** `paused` stops bookings and keeps
 * the page; archiving removes the city from pickers and keeps everything already pointing at it.
 * Each is a different sentence to a devotee who has the page bookmarked.
 *
 * **A service area is the coarse half of matching.** Area membership decides who is even
 * considered; the radius from a pandit's service point decides who is close enough. Both must
 * pass — the intersection, never the union — so an area with nobody in it is a city that looks
 * live and dispatches nothing.
 */
import { useState, type ReactNode } from 'react';
import { Chip, Note, StatusChip } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { DataTable, DetailPage } from '../components/data';
import { KV } from '../components/entities';
import { ConfirmAction, FilterBar, ScopeAction, Section } from '../components/kit';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { QUEUE_HINT, useQueue } from '../lib/useQueue';
import { count } from '../lib/text';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import {
  useArchiveCity,
  useCities,
  useCreateServiceArea,
  useDeleteServiceArea,
  usePatchServiceArea,
  useSetLaunchStage,
  useServiceAreas,
  useTravelZones,
} from '../lib/api/hooks/geo';
import { useSupplyLiquidity } from '../lib/api/hooks/supply';
import { CityIdentityForm, LocalitiesPanel, NewCityForm, ServiceAreaEditForm, TravelZonesPanel, WaitlistPanel } from './CityEditors';
import { COVERAGE_SCOPES } from './Dashboard';
import { coverLabel, coverTone } from './opsBoard';
import { AuditTrailPanel } from '../components/audit';
import { useUrlTab } from '../lib/filterCodecs';

const PAGE_SIZE = 50;

/**
 * The server's own stage enum. Ordered as a market actually moves through them, which is not
 * alphabetical and is the order an ops lead reads.
 */
const STAGES = ['not_planned', 'seo_only', 'waitlist', 'pilot', 'live', 'paused'] as const;
type Stage = (typeof STAGES)[number];

const TABS = [
  { key: '', label: 'All' },
  { key: 'live', label: 'Live' },
  { key: 'pilot', label: 'Pilot' },
  { key: 'waitlist', label: 'Waitlist' },
  { key: 'seo_only', label: 'SEO only' },
  { key: 'paused', label: 'Paused' },
] as const;

/** The tab rides the URL (doc 05-admin/04 §3.3), so a pasted link opens the same queue. */
const TAB_KEYS = TABS.map((t) => t.key);

const STAGE_TONE: Record<string, 'success' | 'warn' | 'error' | 'neutral'> = {
  live: 'success',
  pilot: 'warn',
  waitlist: 'warn',
  paused: 'error',
  seo_only: 'neutral',
  not_planned: 'neutral',
};

export function stageTone(stage: string): 'success' | 'warn' | 'error' | 'neutral' {
  return STAGE_TONE[stage] ?? 'neutral';
}

export function stageLabel(stage: string): string {
  return stage.replace(/_/g, ' ');
}

/**
 * What moving to this stage actually does, in the words an ops lead needs before pressing it.
 * Only `live` and `paused` flip `is_launched`, and saying so is the point — the others change what
 * the website says and nothing about whether a booking can be taken.
 */
const STAGE_CONSEQUENCE: Record<Stage, string> = {
  not_planned: 'The city disappears from the website. Nothing was bookable, so nothing stops.',
  seo_only: 'The page stays and stays truthful; no booking button. A devotee can read, not book.',
  waitlist: 'The page collects interest instead of bookings. Everyone who signs up is notified when it opens.',
  pilot: 'Bookings open. Treat it as live for anybody who books — a pilot is not a lower promise to a devotee.',
  live: 'Bookings open, and the waitlist is notified at the next worker run.',
  paused: 'Bookings stop immediately. The page stays up and says so; bookings already confirmed are untouched.',
};

/** The moves that open or close booking: typed with the city's slug, so it is the right city. */
const BOOKING_SWITCHES: ReadonlySet<string> = new Set(['pilot', 'live', 'paused']);

export function stagePhrase(target: Stage, slug: string): string {
  return BOOKING_SWITCHES.has(target) ? slug : target.toUpperCase().replace(/_/g, '');
}

/**
 * What the server's launch gate will ask for, read from the lists this screen already holds
 * (admin doc 12 §3.2). In the server's own words. A list still loading is not "none": the server
 * stays the backstop, and this only speaks when it knows.
 */
export function stagePreflight(
  target: Stage,
  areas: readonly { is_active: boolean }[] | undefined,
  zones: readonly { is_active: boolean }[] | undefined,
): string[] {
  if (target !== 'pilot' && target !== 'live') return [];
  const missing: string[] = [];
  if (areas && !areas.some((a) => a.is_active)) missing.push('at least one active service area');
  if (zones && !zones.some((z) => z.is_active)) missing.push('at least one active travel zone');
  return missing;
}

const DETAIL_TABS = ['Service areas', 'Travel zones', 'Localities', 'Waitlist', 'History'];

type Liquidity = ReturnType<typeof useSupplyLiquidity>;

/**
 * The design's supply rows on a city: its dispatchable roster, the week's open demand and cover.
 *
 * Only a launched city is in the liquidity read — an unlaunched one has no roster to speak of — so
 * for anything else there are no rows rather than three zeros. Nor are there for a scope the read
 * refuses, or a read that failed. A read still in flight is a dash.
 */
export function supplyRows(liquidity: Liquidity, cityId: number, shown: boolean): [string, ReactNode][] {
  if (!shown || liquidity.isError) return [];
  const row = liquidity.data?.items.find((c) => c.city_id === cityId);
  if (!row) return liquidity.isSuccess ? [] : [['Dispatchable pandits', '—']];
  return [
    ['Dispatchable pandits', String(row.dispatchable)],
    ['Open demand, 7 days', `${row.open_demand} ${row.open_demand === 1 ? 'booking' : 'bookings'}`],
    ['Coverage', <StatusChip key="cover" tone={coverTone(row.covered_share)} label={coverLabel(row.covered_share)} />],
  ];
}

export function Cities() {
  const { scope, toast } = useConsole();
  const seesSupply = (COVERAGE_SCOPES as readonly string[]).includes(scope);
  const liquidity = useSupplyLiquidity(seesSupply);
  const [tab, setTab] = useUrlTab('cities', TAB_KEYS, '');
  const [detailTab, setDetailTab] = useState('Service areas');
  const [staging, setStaging] = useState<Stage | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [areaName, setAreaName] = useState('');
  const [areaLat, setAreaLat] = useState('');
  const [areaLng, setAreaLng] = useState('');
  const [areaRadius, setAreaRadius] = useState('10');
  const [creating, setCreating] = useState(false);
  const [editingArea, setEditingArea] = useState<number | null>(null);
  const [deletingArea, setDeletingArea] = useState<{ id: number; name: string } | null>(null);
  const [editing, setEditing] = useState(false);

  const cities = useCities({ page: 1, page_size: PAGE_SIZE, launch_stage: tab || undefined });
  const rows = cities.data?.items ?? [];
  const queue = useQueue(rows, { key: tab });
  const { focus, setFocus, setOpen } = queue;
  /* The queue holds the row as it was clicked; after an edit the list refetches, and the detail
     should show the saved values rather than the snapshot. */
  const open = queue.open && (rows.find((c) => c.id === queue.open!.id) ?? queue.open);

  const areas = useServiceAreas(open?.id);
  const zones = useTravelZones(open?.id);

  const setStage = useSetLaunchStage();
  const archive = useArchiveCity();
  const createArea = useCreateServiceArea();
  const patchArea = usePatchServiceArea();
  const deleteArea = useDeleteServiceArea();

  const outcome = (message: string, tone: 'success' | 'info' | 'error' = 'success') => ({
    onSuccess: () => toast({ tone, message }),
    onError: (e: unknown) => toast({ tone: 'error' as const, message: errorMessage(e) }),
  });

  const stageDialog = open && staging && (
    <ConfirmAction
      open
      title={`Move ${open.name} to ${stageLabel(staging)}?`}
      phrase={stagePhrase(staging, open.slug)}
      tone={staging === 'paused' || staging === 'not_planned' ? 'error' : 'warn'}
      blocked={stagePreflight(staging, areas.data, zones.data)}
      reason
      consequences={[
        STAGE_CONSEQUENCE[staging],
        'The stage and the bookable flag are kept consistent by the server — you cannot leave the city saying one thing and doing another.',
      ]}
      onCancel={() => setStaging(null)}
      onConfirm={(reason: string) => {
        const stage = staging;
        setStaging(null);
        setStage.mutate(
          { cityId: open.id, stage, reason },
          outcome(`${open.name} is now ${stageLabel(stage)}`, stage === 'paused' ? 'info' : 'success'),
        );
      }}
      confirmLabel={`Move to ${stageLabel(staging)}`}
    />
  );

  const archiveDialog = open && (
    <ConfirmAction
      open={archiving}
      title={`Archive ${open.name}?`}
      phrase="ARCHIVE"
      tone="error"
      reason
      consequences={[
        'Removes the city from every picker. It is not deleted: pandits, bookings and waitlist entries pointing at it still resolve.',
        'The public page goes at the next site build.',
        'The server refuses while the city still has localities or service areas — delete those first.',
      ]}
      onCancel={() => setArchiving(false)}
      onConfirm={(reason: string) => {
        setArchiving(false);
        /* An archived city leaves the list, so its detail would fall back to the click-time
           snapshot and go on offering a stage and Archive. Close it once the server agrees. */
        const done = outcome(`${open.name} archived`, 'info');
        archive.mutate(
          { cityId: open.id, reason },
          {
            ...done,
            onSuccess: () => {
              setOpen(null);
              done.onSuccess();
            },
          },
        );
      }}
      confirmLabel="Archive city"
    />
  );

  const deleteAreaDialog = (
    <ConfirmAction
      open={deletingArea !== null}
      title={`Delete ${deletingArea?.name ?? 'this area'}?`}
      phrase="DELETE"
      tone="error"
      consequences={[
        'Gone for good — only for an area made in error. Pause it instead to stop dispatch while keeping it.',
        'The server refuses while a pandit serves it, or when it is the last area of a launched city.',
      ]}
      onCancel={() => setDeletingArea(null)}
      onConfirm={() => {
        const area = deletingArea!;
        setDeletingArea(null);
        deleteArea.mutate(area.id, outcome(`${area.name} deleted`, 'info'));
      }}
      confirmLabel="Delete area"
    />
  );

  if (open) {
    return (
      <DetailPage
        title={open.name}
        code={open.slug}
        status={<StatusChip tone={stageTone(open.launch_stage)} label={stageLabel(open.launch_stage)} />}
        tabs={DETAIL_TABS}
        activeTab={detailTab}
        onTab={setDetailTab}
        onClose={() => {
          setOpen(null);
          setEditing(false);
        }}
        actions={
          <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Select
              label="Move to stage"
              value={stageLabel(open.launch_stage)}
              options={STAGES.filter(
                (sg) => sg === open.launch_stage || open.allowed_next_stages?.includes(sg),
              ).map(stageLabel)}
              onChange={(label) => {
                const next = STAGES.find((sg) => stageLabel(sg) === label)!;
                if (next !== open.launch_stage) setStaging(next);
              }}
            />
            <ScopeAction scope={scope} need="catalogue_content" size="sm" onClick={() => setArchiving(true)}>
              Archive
            </ScopeAction>
          </div>
        }
      >
        <Section
          title="Where this city stands"
          actions={
            !editing && (
              <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="secondary" onClick={() => setEditing(true)}>
                Edit city
              </ScopeAction>
            )
          }
        >
          <KV
            rows={[
              ['Stage', stageLabel(open.launch_stage)],
              /* The two halves, side by side on purpose: this pairing is the thing that must never
                 disagree, and showing only one of them is how a contradiction goes unnoticed. */
              ['Taking bookings', open.is_launched ? 'Yes' : 'No'],
              ['Launched on', open.launched_on ?? 'never'],
              ['Centre', `${open.latitude}, ${open.longitude}`],
              ['Radius', `${open.radius_km} km`],
              ['Metro', open.is_metro ? 'Yes' : 'No'],
              ['Population', open.population === null || open.population === undefined ? 'unknown' : open.population.toLocaleString('en-IN')],
              ...supplyRows(liquidity, open.id, seesSupply && open.is_launched),
            ]}
          />
          {editing && <CityIdentityForm key={open.id} city={open} onDone={() => setEditing(false)} />}
        </Section>

        {detailTab === 'Service areas' && (
          <Section title="Service areas">
            <Note>
              The coarse half of matching: area membership decides who is considered, the radius
              from a pandit’s own service point decides who is close enough. Both must pass.
            </Note>
            {areas.isPending && <LoadingState message="Reading service areas…" />}
            {areas.isError && <ErrorState title="Could not read service areas" cause={errorMessage(areas.error)} />}
            {areas.isSuccess && areas.data.length === 0 && (
              <EmptyState
                icon="map-pin"
                title="No area drawn yet"
                body="A city with no service area can be opened for applications but cannot dispatch — the activation gate holds every pandit until one exists."
                inline
              />
            )}
            {areas.data && areas.data.length > 0 && (
              <div style={{ display: 'grid', gap: 'var(--sp-xs)', marginTop: 'var(--sp-md)' }}>
                {areas.data.map((a) => (
                  <div key={a.id}>
                  <div
                    style={{
                      display: 'flex',
                      gap: 'var(--sp-sm)',
                      alignItems: 'center',
                      padding: 'var(--sp-sm) var(--sp-md)',
                      border: '1px solid var(--c-border)',
                      borderRadius: 'var(--r-tile)',
                      background: 'var(--c-surface2)',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>{a.name}</div>
                      <div style={{ color: 'var(--c-ink2)', fontSize: 'var(--fs-admin-colhead)' }}>
                        {a.latitude}, {a.longitude} · {a.radius_km} km
                        {a.notes ? ` · ${a.notes}` : ''}
                      </div>
                    </div>
                    <StatusChip tone={a.is_active ? 'success' : 'neutral'} label={a.is_active ? 'active' : 'paused'} />
                    <ScopeAction
                      scope={scope}
                      need="catalogue_content"
                      size="sm"
                      onClick={() =>
                        patchArea.mutate(
                          { areaId: a.id, patch: { is_active: !a.is_active } },
                          outcome(a.is_active ? `${a.name} paused` : `${a.name} resumed`),
                        )
                      }
                    >
                      {a.is_active ? 'Pause' : 'Resume'}
                    </ScopeAction>
                    <ScopeAction
                      scope={scope}
                      need="catalogue_content"
                      size="sm"
                      variant="ghost"
                      ariaLabel={`Edit ${a.name}`}
                      onClick={() => setEditingArea(editingArea === a.id ? null : a.id)}
                    >
                      Edit
                    </ScopeAction>
                    <ScopeAction
                      scope={scope}
                      need="catalogue_content"
                      size="sm"
                      variant="ghost"
                      ariaLabel={`Delete ${a.name}`}
                      onClick={() => setDeletingArea({ id: a.id, name: a.name })}
                    >
                      Delete
                    </ScopeAction>
                  </div>
                  {editingArea === a.id && <ServiceAreaEditForm area={a} onDone={() => setEditingArea(null)} />}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'flex-end', marginTop: 'var(--sp-md)', flexWrap: 'wrap' }}>
              <Input label="Area name" value={areaName} onChange={setAreaName} density="admin" />
              <Input label="Latitude" value={areaLat} onChange={setAreaLat} density="admin" />
              <Input label="Longitude" value={areaLng} onChange={setAreaLng} density="admin" />
              <Input label="Radius km" value={areaRadius} onChange={setAreaRadius} density="admin" />
              <ScopeAction
                scope={scope}
                need="catalogue_content"
                size="sm"
                disabled={!areaName || !areaLat || !areaLng}
                onClick={() =>
                  createArea.mutate(
                    {
                      cityId: open.id,
                      body: {
                        name: areaName,
                        latitude: areaLat,
                        longitude: areaLng,
                        radius_km: Number(areaRadius),
                      },
                    },
                    {
                      onSuccess: () => {
                        setAreaName('');
                        setAreaLat('');
                        setAreaLng('');
                        toast({ tone: 'success', message: `${areaName} added` });
                      },
                      onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
                    },
                  )
                }
              >
                Add area
              </ScopeAction>
            </div>
          </Section>
        )}

        {detailTab === 'Travel zones' && <TravelZonesPanel cityId={open.id} />}

        {detailTab === 'Localities' && <LocalitiesPanel cityId={open.id} />}

        {detailTab === 'Waitlist' && <WaitlistPanel cityId={open.id} />}
        {detailTab === 'History' && <AuditTrailPanel entityType="city" entityId={open.id} />}

        {stageDialog}
        {archiveDialog}
        {deleteAreaDialog}
      </DetailPage>
    );
  }

  if (creating) return <NewCityForm onDone={() => setCreating(false)} />;

  return (
    <>
      <FilterBar
        tabs={TABS.map((t) => ({ key: t.key, label: t.label }))}
        tab={tab}
        onTab={setTab}
        right={
          <ScopeAction scope={scope} need="catalogue_content" size="sm" icon="plus" onClick={() => setCreating(true)}>
            New city
          </ScopeAction>
        }
      />

      {cities.isPending && <LoadingState message="Reading cities…" />}
      {cities.isError && <ErrorState title="Could not read cities" cause={errorMessage(cities.error)} />}
      {cities.isSuccess && rows.length === 0 && (
        <EmptyState icon="building" title="No city at this stage" />
      )}
      {rows.length > 0 && (
        <DataTable
          columns={[
            { key: 'name', label: 'City' },
            { key: 'stage', label: 'Stage' },
            { key: 'bookable', label: 'Taking bookings' },
            { key: 'radius', label: 'Radius' },
            { key: 'waitlist', label: 'Waitlist', align: 'right', mono: true },
          ]}
          rows={rows.map((c) => ({
            id: c.id,
            name: c.name,
            stage: <StatusChip tone={stageTone(c.launch_stage)} label={stageLabel(c.launch_stage)} />,
            bookable: <Chip label={c.is_launched ? 'Yes' : 'No'} />,
            radius: `${c.radius_km} km`,
            waitlist: c.waitlist_count == null ? '—' : c.waitlist_count.toLocaleString('en-IN'),
          }))}
          focusedIndex={focus}
          onFocusRow={setFocus}
          onRowClick={(r) => setOpen(rows.find((c) => c.id === r.id)!)}
          footer={`${count(cities.data!.total, 'city', 'cities')} · ${QUEUE_HINT}`}
        />
      )}
    </>
  );
}
